import type { Chapter, EditorResult, Ledger, PublicationEditorResult, StoryBible, ValidationResult } from '../../types';
import type { OriginalOrchestrationLoopState } from '../golden/goldenOrchestrationLoop';

export const ORIGINAL_COMPAT_CHECKPOINT_VERSION = 1;
export const ORIGINAL_COMPAT_CHECKPOINT_RETENTION_MS = 7 * 24 * 60 * 60 * 1000;

export type OriginalCompatResumeStage =
  | 'SCRIBE_COMPLETE'
  | 'INSPECTOR_COMPLETE'
  | 'EDITOR_COMPLETE'
  | 'PUBLICATION_EDITOR_COMPLETE'
  | 'ARCHIVIST_COMPLETE'
  | 'COMMIT_PENDING';

export interface OriginalCompatCheckpointBody {
  checkpointVersion: number;
  engineMode: 'ORIGINAL_ENGINE_COMPAT';
  projectId: string;
  chapterNumber: number;
  runId: string;
  resumeStage: OriginalCompatResumeStage;
  chapterDraft: Chapter;
  inspectorResult?: ValidationResult;
  correctionOrder?: string;
  originalRetryCount: number;
  loopState?: Partial<OriginalOrchestrationLoopState>;
  editorResult?: EditorResult;
  publicationResult?: PublicationEditorResult;
  updatedLedger?: Ledger;
  authoritativeInputHash: string;
  roadmapHash: string;
  createdAt: string;
  expiresAt: string;
  canonical: false;
}

export interface OriginalCompatCheckpointMetadata extends Omit<OriginalCompatCheckpointBody, 'chapterDraft' | 'inspectorResult' | 'editorResult' | 'publicationResult' | 'updatedLedger' | 'loopState'> {
  storagePath: string;
  deletedAt?: string;
}

export interface OriginalCompatLoadedProject {
  projectId: string;
  metadata: Record<string, unknown>;
  storyBible: StoryBible;
  ledger: Ledger;
  chapters: Chapter[];
  currentChapterNumber: number;
  volume: number;
  seriesId?: string;
  previousVolumeId?: string;
  entitlementState: {
    exportUnlocked?: boolean;
    manuscriptTier?: string;
    maxChapters?: number;
  };
  recoverableCheckpoint: OriginalCompatCheckpointMetadata | null;
  roadmapChapterCount: number;
}

export interface OriginalCompatCommitKey {
  projectId: string;
  chapterNumber: number;
  runId: string;
}

export function buildCommitKey(input: OriginalCompatCommitKey): string {
  return `${input.projectId}:${input.chapterNumber}:${input.runId}`;
}

export interface OriginalCompatCommitAudit {
  projectId: string;
  chapterNumber: number;
  runId: string;
  commitKey: string;
  chapterStorageStatus: 'written' | 'skipped' | 'failed';
  firestoreStatus: 'written' | 'skipped' | 'failed';
  idempotentReplay: boolean;
  reservationAction: 'none' | 'consume' | 'pending';
}

export interface OriginalCompatProjectOpenAudit {
  projectId: string;
  currentChapterNumber: number;
  chapterCount: number;
  roadmapChapterCount: number;
  volume: number;
  hasRecoverableCheckpoint: boolean;
}

export type CheckpointIntegrityResult =
  | { ok: true }
  | { ok: false; reason: 'hash_mismatch' | 'expired' | 'version_mismatch' | 'engine_mode' | 'missing_body' };
